import { useCallback, useEffect, useRef } from "react";
import Icon from "./Icon";

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';

/**
 * Diálogo accesible: bloquea el scroll del fondo, atrapa el foco,
 * cierra con Escape y devuelve el foco al elemento que lo abrió.
 * En un sitio de una sola página es lo que permite mostrar el
 * detalle de un producto o un artículo sin perder el contexto.
 */
export default function Modal({ open, onClose, title, size = "md", children }) {
  const panelRef = useRef(null);
  const previousFocus = useRef(null);

  const handleKeyDown = useCallback(
    (event) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        onClose();
        return;
      }

      if (event.key !== "Tab") return;

      const nodes = panelRef.current?.querySelectorAll(FOCUSABLE);
      if (!nodes || nodes.length === 0) return;

      const first = nodes[0];
      const last = nodes[nodes.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    },
    [onClose]
  );

  useEffect(() => {
    if (!open) return undefined;

    previousFocus.current = document.activeElement;
    document.body.classList.add("fsg-no-scroll");

    const timer = window.setTimeout(() => {
      const target =
        panelRef.current?.querySelector("[data-autofocus]") ||
        panelRef.current;
      target?.focus();
    }, 20);

    return () => {
      window.clearTimeout(timer);
      document.body.classList.remove("fsg-no-scroll");
      if (previousFocus.current instanceof HTMLElement) {
        previousFocus.current.focus();
      }
    };
  }, [open]);

  if (!open) return null;

  return (
    <div className="fsg-modal" onKeyDown={handleKeyDown}>
      <button
        type="button"
        className="fsg-modal__backdrop"
        aria-label="Cerrar"
        onClick={onClose}
      />
      <div
        ref={panelRef}
        className={`fsg-modal__panel is-${size}`}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
      >
        <div className="fsg-modal__head">
          <h2>{title}</h2>
          <button
            type="button"
            className="fsg-modal__close"
            onClick={onClose}
            data-autofocus
          >
            <Icon name="close" />
            <span className="fsg-sr-only">Cerrar</span>
          </button>
        </div>
        <div className="fsg-modal__body">{children}</div>
      </div>
    </div>
  );
}
